import React, {memo, useCallback} from 'react';
import {ChevronArrowDownIcon, ClockIcon, EmailIcon, PhoneIcon} from './Icons';

interface FormData {
    name: string;
    email: string;
    phone: string;
    findUs: string;
}

interface FormErrors {
    name?: string;
    email?: string;
    phone?: string;
}

export const EnquiryForm: React.FC<{ className?: string, forModal?: boolean }> = memo(({className = "", forModal = false}) => {
    const [formData, setFormData] = React.useState<FormData>({
        name: '',
        email: '',
        phone: '',
        findUs: '',
    });
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [submitMessage, setSubmitMessage] = React.useState('');

    const validateForm = useCallback((data: FormData): FormErrors => {
        const newErrors: FormErrors = {};

        if (!data.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!data.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!data.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[+]?[\d\s\-()]{10,}$/.test(data.phone)) {
            newErrors.phone = 'Please enter a valid phone number';
        }

        return newErrors;
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({...prev, [name]: value}));

        if (errors[name as keyof FormErrors]) {
            setErrors(prev => ({...prev, [name]: undefined}));
        }
    }, [errors]);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();

        const validationErrors = validateForm(formData);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage('');

        try {
            // TODO: Simulating API call
            await new Promise(resolve => setTimeout(resolve, 1000));

            setSubmitMessage('Thank you for your enquiry! We will get back to you soon.');
            setFormData({name: '', email: '', phone: '', findUs: ''});
            setErrors({});
        } catch (error) {
            setSubmitMessage('Sorry, there was an error submitting your enquiry. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, validateForm]);

    return (
        <div className={`text-royal-pine z-10 ${className}`}>
            <h2 className={`heading-font text-3xl sm:text-4xl ${!forModal ? 'lg:text-5xl lg:text-left': ''} mb-6 sm:mb-8 text-center`}>
                CONTACT US
            </h2>

            {submitMessage && (
                <div className={`mb-6 p-4 rounded-md ${
                    submitMessage.includes('Thank You')
                        ? 'bg-green-50 text-green-800 border border-green-200'
                        : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                    {submitMessage}
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-2 md:space-y-4 lg:space-y-6" noValidate>
                {/* Name Field */}
                <div>
                    <label htmlFor="name" className="sr-only">Name</label>
                    <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Name *"
                        className={`w-full p-3 border text-sm sm:text-base transition-colors focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 ${
                            errors.name
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-300 focus:border-royal-pine'
                        }`}
                        disabled={isSubmitting}
                        required
                        aria-invalid={!!errors.name}
                        aria-describedby={errors.name ? 'name-error' : undefined}
                    />
                    {errors.name && (
                        <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
                            {errors.name}
                        </p>
                    )}
                </div>

                {/* Email Field */}
                <div>
                    <label htmlFor="email" className="sr-only">Email</label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Email *"
                        className={`w-full p-3 border text-sm sm:text-base transition-colors focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 ${
                            errors.email
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-300 focus:border-royal-pine'
                        }`}
                        disabled={isSubmitting}
                        required
                        aria-invalid={!!errors.email}
                        aria-describedby={errors.email ? 'email-error' : undefined}
                    />
                    {errors.email && (
                        <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
                            {errors.email}
                        </p>
                    )}
                </div>

                {/* Phone Field */}
                <div>
                    <label htmlFor="phone" className="sr-only">Phone</label>
                    <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="Phone *"
                        className={`w-full p-3 border text-sm sm:text-base transition-colors focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 ${
                            errors.phone
                                ? 'border-red-300 focus:border-red-500'
                                : 'border-gray-300 focus:border-royal-pine'
                        }`}
                        disabled={isSubmitting}
                        required
                        aria-invalid={!!errors.phone}
                        aria-describedby={errors.phone ? 'phone-error' : undefined}
                    />
                    {errors.phone && (
                        <p id="phone-error" className="mt-1 text-sm text-red-600" role="alert">
                            {errors.phone}
                        </p>
                    )}
                </div>

                {/* Source Field */}
                <div className="relative">
                    <label htmlFor="findUs" className="sr-only">How did you find us?</label>
                    <select
                        id="findUs"
                        name="findUs"
                        value={formData.findUs}
                        onChange={handleInputChange}
                        className={`w-full p-3 border border-gray-300 text-sm sm:text-base focus:outline-none focus:border-royal-pine focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 transition-colors appearance-none bg-white`}
                        disabled={isSubmitting}
                    >
                        <option value="" selected disabled>How did you find us?</option>
                        <option value="online-search">Online Search</option>
                        <option value="instagram">Instagram</option>
                        <option value="facebook">Facebook</option>
                        <option value="member-referral">Member Referral</option>
                        <option value="word-of-mouth">Word of Mouth</option>
                    </select>
                    <div
                        className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                        <ChevronArrowDownIcon className="w-4 h-4 sm:w-5 sm:h-5"/>
                    </div>
                </div>

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-royal-pine text-ivory py-3 px-6 text-sm sm:text-base font-medium rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-royal-pine focus:ring-opacity-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? 'SENDING...' : 'SEND'}
                </button>
            </form>

            {/* Contact Information Grid */}
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3">
                <div
                    className="flex flex-row sm:flex-col items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                    <PhoneIcon
                        className="w-6 h-6 text-black flex-shrink-0 group-hover:text-royal-pine transition-colors"/>
                    <p className="flex flex-col min-w-0">
                        <span
                            className="text-sm text-start sm:text-center text-black font-medium">Office Mobile No.</span>
                        <a
                            href="tel:+919310821204"
                            className="text-sm text-start sm:text-center text-royal-pine hover:text-logo-color transition-colors">
                            +91 93108 21204
                        </a>
                        <a
                            href="tel:+919311101204"
                            className="text-sm text-start sm:text-center text-royal-pine hover:text-logo-color transition-colors">
                            +91 93111 01204
                        </a>
                    </p>
                </div>

                <div className="flex flex-row sm:flex-col items-center gap-3 p-3 rounded-lg">
                    <ClockIcon className="w-6 h-6 text-black flex-shrink-0"/>
                    <p className="flex flex-col min-w-0">
                        <span className="text-sm text-start sm:text-center text-black font-medium">Office Hours</span>
                        <span className="text-sm text-start sm:text-center text-royal-pine">11.00 AM to 7:00 PM</span>
                    </p>
                </div>

                <a
                    href="mailto:contact@allacesclub.com"
                    className="flex flex-row sm:flex-col items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                    <EmailIcon
                        className="w-6 h-6 text-black flex-shrink-0 group-hover:text-royal-pine transition-colors"/>
                    <p className="flex flex-col min-w-0">
                        <span className="text-sm text-start sm:text-center text-black font-medium">Email</span>
                        <span
                            className="text-sm text-start sm:text-center text-royal-pine group-hover:text-logo-color transition-colors break-all">
                            contact@allacesclub.com
                        </span>
                    </p>
                </a>
            </div>
        </div>
    );
});

EnquiryForm.displayName = "EnquiryForm";